import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

import { emptyPlatform, IPlatform } from "../../../model/platform";
import { emptyPlatformFilter, IPlatformFilter } from "../../../model/platform-filter";
import { PlatformService } from "../../../services/platform.service";
import { LoginService } from "../../../services/login.service";
import { IHttpErrorsResponse } from "../../../model/http-errors-response";
import { ICert } from "../../../model/cert";
import { PlatformCertService } from "../../../services/platform-cert.service";
import { IPlatformCerts } from "../../../model/platform-certs";
import { ActivatedRoute, Params } from "@angular/router";

@Component({
  selector: 'app-inv-platform-list',
  templateUrl: './inv-platform-list.component.html',
  styleUrls: ['./inv-platform-list.component.css']
})
export class InvPlatformListComponent implements OnInit {

  public MAX_PLATFORMS = 5;

  /**
   * Contenido del panel modal de nueva plataforma
   */
  @ViewChild('addPlatform')
  addPlatform: TemplateRef<any>;
  addPlatformModal: NgbModalRef;
  errors: IHttpErrorsResponse;

  /**
   * Contenido del panel modal para eliminar una plataforma
   */
  @ViewChild('removePlatform')
  removePlatform: TemplateRef<any>;

  /**
   * Contenido del panel modal para editar una plataforma
   */
  @ViewChild('editPlatform')
  editPlatform: TemplateRef<any>;

  /**
   * @type {boolean}
   * Variable para controlar que se ha incorporado un fichero al formulario
   */
  isFileLoaded: boolean = false;

  /**
   * listado de plataformas
   *
   * @type {IPlatform[]}
   */
  platforms: IPlatform[] = [];

  /**
   * filtro de plataformas
   *
   * @type {any}
   */
  platformFilter: IPlatformFilter = emptyPlatformFilter;

  /**
   * @type {IPlatform}
   */
  platform: IPlatform = emptyPlatform;

  /**
   * Identificador de Invoicing Service
   */
  identifier: string;

  /**
   *
   * @type {number}
   */
  itemsPerPage: number = 5;

  /**
   *
   * @type {number}
   */
  p: number = 0;

  /**
   * listado de certificados
   *
   * @type {ICert[]}
   */
  certificates: IPlatformCerts[] = [];

  /**
   * constructor
   * @param {NgbModal} modalService
   * @param {LoginService} loginService
   * @param {PlatformService} platformService
   * @param {PlatformCertService} platformCertService
   * @param route
   */
  constructor(
    private modalService: NgbModal,
    private loginService: LoginService,
    private platformService: PlatformService,
    private platformCertService: PlatformCertService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    this.route.params
      .subscribe((params: Params) => {
          this.identifier = params['identifier'];
          this.platformService.getPlatforms(this.identifier)
            .subscribe(res => this.platforms = res);
      });
  }

  /**
   * Abre el panel modal de creación de una plataforma
   */
  openAddPlatform() {
    this.clearCreatePlatform();
    this.addPlatformModal = this.modalService.open(this.addPlatform);
    this.addPlatformModal.result.then();
  }

  /**
   * Abre el panel modal para eliminar una plataforma   *
   * @param {IPlatform} platform
   * @param {number} index
   */
  openDeletePlatform(platform: IPlatform, index: number) {
    this.modalService.open(this.removePlatform).result.then((result) => {
      if (result === 'remove') {
        this.platformService.deletePlatform(this.identifier, platform)
          .subscribe(() => this.platforms.splice(index, 1));
      }
    });
  }

  /**
   * Abre el panel modal para editar una plataforma
   * @param {IPlatform} platform
   */
  openEditPlatform(platform: IPlatform) {
    this.errors = null;
    this.platform = platform;
    this.modalService.open(this.editPlatform);

    this.addFileUpload(this.platform);
  }

  /**
   * Añade un nuevo certificado
   */
  onAddCertificateClicked() {
    this.addCertificate();
  }

  /**
   * Anade un nuevo certificado al modal
   */
  addCertificate() {
    if (this.certificates.length < this.MAX_PLATFORMS && this.certificates.length < 1) {
      // Inicializa un nuevo elemento
      this.certificates.push(
        {
          'certificate': {
            'id': -1,
            'file': '',
            'issuer': '',
          }
        });
    }
  }

  /**
   * Permite editar el nombre de la plataforma
   * @param {IPlatform} platform
   */
  editPlatformName(platform: IPlatform) {
    this.platformService.updatePlatform(platform, platform.receivers, this.identifier)
      .subscribe(
        () => {},
        error => {
          this.errors = error;
        }
      );
  }

  /**
   * Borra la asociación entre plataforma y certificado (platformCert) en la edición de la plataforma
   * @param {IPlatform} platform
   * @param {number} index
   */
  onDeleteCertificateClicked(platform: IPlatform, index: number) {
    this.platformCertService.deletePlatformCert(this.identifier, platform, index)
      .subscribe(() => {
        platform.platformCerts.splice(index, 1);
        this.addFileUpload(platform);
      }
    );
  }

  /**
   * Elimina el elemento html que permite seleccionar un certificado a la plataforma
   * @param index
   */
  onRemoveCertificateClicked(index: number) {
    this.errors = null;
    this.certificates.splice(index,1);
    this.addFileUpload(this.platform);
  }

  /**
   * Realiza el guardado de la plataforma
   */
  savePlatform() {
    this.platformService.savePlatform(this.identifier, this.platform)
      .subscribe(
        result => {
          this.platforms.push(result);
          this.addPlatformModal.close();
        },
        error => {
          this.errors = error;
        });
  }

  /**
   * @param event
   * @param {IPlatform} platform
   * @param {ICert} certificate
   */
  addOptionalFile(event, platform, certificate) {
    this.uploadFile(event).then(()=>{
      this.platformCertService.addCertificate(this.identifier, platform, certificate.splice(1,1).pop())
        .subscribe(
          result => {
            this.errors = null;
            this.platform.platformCerts.push(result);
            this.certificates.splice(0, 1);
          },
          error => {
            this.errors = error;
          })
      },
    )
  }

  /**
   * Obtiene los datos del certificado seleccionado.
   * NOTA: La estracción (función uploadFile) se incluye en una promesa para que el envío de los datos se haga siempre
   * una vez que se ha realizado la extracción de los datos
   *
   * @param event
   */
  addFile(event) {
    this.clearCerts();
    this.uploadFile(event).then(()=>{
      this.platform.platformCerts.push(this.certificates.pop());
      this.isFileLoaded = true;
      }
    )
  }

  /**
   * @param event
   * @returns {Promise<any>}
   */
  uploadFile(event):Promise<any> {
    return new Promise<any>(resolve => {
      let reader = new FileReader();
      if (event.target.files && event.target.files.length > 0) {
        let file = event.target.files[0];
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.certificates.push({'certificate': {'id': -1, 'file': reader.result.split(',')[1], 'issuer': file.name}});
          resolve();
        }
      }
    })
  };

  private clearCreatePlatform() {
    this.clearCerts();

    this.errors = null;
    this.platform.name = '';
    this.isFileLoaded = false;
  }

  private clearCerts() {
    for (let index = this.platform.platformCerts.length; index >= 0; index--) {
      this.platform.platformCerts.shift();
    }
  }

  private addFileUpload(platform)
  {
    if (platform.platformCerts.length == 0) {
      this.addCertificate();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/app-manager/invoicing-services/inv-platform-list/inv-platform-list.component.ts